import {AsyncPipe, CurrencyPipe, NgClass} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {EmTranslatePipe, LoggerService, PhonePipe, TranslationService} from '@emma-labs/shared/angular';
import {
  Agent,
  ArchivedPersonInsurance,
  getMonthlyPrice,
  InsuranceFirm,
  MinifiedProposal,
  MinifiedSaleReport,
  PaginatedResponse,
  PaginationParams,
  PersonInsurance,
  ProposalStatus,
  SaleReportStatus,
  trimPhoneNumber,
  UserRole,
} from '@emma-labs/shared/typescript';
import {MenuItem, SelectItem} from 'primeng/api';
import {CardModule} from 'primeng/card';
import {ContextMenuModule} from 'primeng/contextmenu';
import {InputTextModule} from 'primeng/inputtext';
import {MultiSelectModule} from 'primeng/multiselect';
import {SkeletonModule} from 'primeng/skeleton';
import {Table, TableModule, TableRowSelectEvent} from 'primeng/table';
import {TagModule} from 'primeng/tag';
import {BehaviorSubject, combineLatest, forkJoin, Observable, of, Subject, switchMap} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  map,
  shareReplay,
  startWith,
  takeUntil,
} from 'rxjs/operators';
import {AgentService} from '../../../services/agent.service';
import {AuthService} from '../../../services/auth.service';
import {ProposalService} from '../../../services/proposal/proposal.service';
import {SaleReportService} from '../../../services/sale/sale-report.service';
import {getProducts, Product, ProductType} from '../../../utils/product-type';
import {PROPOSALS_STATUS_OPTIONS} from '../proposals-status';
import {TableEntry} from './table-entry';

interface PaginatedTableData {
  entries: TableEntry[];
  totalRecords: number;
  loading: boolean;
}

@Component({
  selector: 'em-new-table-section',
  templateUrl: './table-section.component.html',
  imports: [
    TableModule,
    CardModule,
    AsyncPipe,
    InputTextModule,
    SkeletonModule,
    CurrencyPipe,
    NgClass,
    PhonePipe,
    FormsModule,
    TagModule,
    MultiSelectModule,
    ContextMenuModule,
  ],
})
export class NewTableSectionComponent implements OnInit, OnDestroy {
  @ViewChild('salesTable') salesTable!: Table;

  tableData$: Observable<PaginatedTableData>;
  agent: Agent | undefined;
  selectedEntry: TableRowSelectEvent | undefined;
  contextMenuItems: MenuItem[] = [];
  matchModeOptions: SelectItem[] = [];
  readonly userRole = UserRole;
  readonly defaultRowsPerPage = 50;
  readonly productType = ProductType;
  readonly proposalsStatusOptions = PROPOSALS_STATUS_OPTIONS;

  // Server-side pagination state
  private readonly paginationParams$ = new BehaviorSubject<PaginationParams>({
    page: 1,
    limit: this.defaultRowsPerPage,
  });
  private readonly searchQuery$ = new BehaviorSubject<string>('');
  private readonly advisorFilter$ = new BehaviorSubject<string>('');
  private readonly statusFilter$ = new BehaviorSubject<ProposalStatus[]>([]);

  // Total counts for sequential pagination
  private proposalCount = 0;
  private saleReportCount = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly translate: TranslationService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly proposalService: ProposalService,
    private readonly saleReportService: SaleReportService,
    private readonly agentService: AgentService,
    private readonly logger: LoggerService,
  ) {
    this.tableData$ = this.initializeTableData();
  }

  ngOnInit(): void {
    this.contextMenuItems = [
      {
        label: this.translate.currentLanguageIsFr()
          ? 'Ouvrir le lien dans un nouvel onglet'
          : 'Open link in new tab',
        icon: 'pi pi-external-link',
        command: () => this.onRowSelect({data: this.selectedEntry}, true),
      },
    ];

    this.matchModeOptions = [
      {label: this.translate.currentLanguageIsFr() ? 'Contient' : 'Contains', value: 'contains'},
      {
        label: this.translate.currentLanguageIsFr() ? 'Ne contient pas' : 'Not contains',
        value: 'notContains',
      },
    ];

    // Initialize state from URL query parameters
    this.initializeFromUrl();

    // Subscribe to parameter changes and update URL
    this.subscribeToStateChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Pagination event handlers
  onPageChange(event: any): void {
    const page = Math.floor(event.first / event.rows) + 1;
    this.paginationParams$.next({
      ...this.paginationParams$.value,
      page,
      limit: event.rows,
    });
  }

  applyFilterGlobal($event: Event): void {
    const value = ($event.target as HTMLInputElement).value.trim();
    const processedValue = this.handlePhoneNumberCase(value);
    this.searchQuery$.next(processedValue);
  }

  onAdvisorFilter(value: string): void {
    this.advisorFilter$.next(value);
  }

  onStatusFilter(values: string[]): void {
    const statusEnums = values.filter((value): value is ProposalStatus =>
      Object.values(ProposalStatus).includes(value as ProposalStatus),
    );
    this.statusFilter$.next(statusEnums);
  }

  getStatusSeverity(status: ProposalStatus): 'success' | 'danger' | 'warning' | 'info' | 'secondary' {
    switch (status) {
      case ProposalStatus.APPROVED:
        return 'success';
      case ProposalStatus.REFUSED:
      case ProposalStatus.ABANDONED:
        return 'danger';
      case ProposalStatus.PENDING_UNDERWRITING:
      case ProposalStatus.PENDING_EXTERNAL_ACTION:
        return 'secondary';
      case ProposalStatus.PENDING_MODIFICATION_DECISION:
      case ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES:
        return 'warning';
      default:
        return 'info';
    }
  }

  sortStatus(event: {data: TableEntry[]; order: number}): void {
    const order = [
      ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES,
      ProposalStatus.PENDING_MODIFICATION_DECISION,
      ProposalStatus.PENDING_UNDERWRITING,
      ProposalStatus.PENDING_CHECKOUT,
      ProposalStatus.ONGOING,
      ProposalStatus.APPROVED,
      ProposalStatus.REFUSED,
      ProposalStatus.ABANDONED,
    ];

    event.data.sort((data1: TableEntry, data2: TableEntry) => {
      const value1 = order.indexOf(data1.status!.value);
      const value2 = order.indexOf(data2.status!.value);
      return event.order * (value1 - value2);
    });
  }

  onRowSelect($event: TableRowSelectEvent, targetBlank = false): void {
    let url: string;

    switch ($event.data.status.value) {
      case ProposalStatus.ONGOING:
      case ProposalStatus.PENDING_CHECKOUT:
        url = `/${['dashboard', 'proposal', btoa($event.data.proposalId)].join('/')}`;
        break;

      case ProposalStatus.APPROVED:
      case ProposalStatus.REFUSED:
      case ProposalStatus.ABANDONED:
      case ProposalStatus.PENDING_UNDERWRITING:
      case ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES:
      case ProposalStatus.PENDING_EXTERNAL_ACTION:
      case ProposalStatus.PENDING_MODIFICATION_DECISION:
        url = `/${['dashboard', 'archived-proposal', btoa($event.data.proposalId)].join('/')}`;
        break;

      default:
        return;
    }

    targetBlank ? window.open(url, '_blank') : this.router.navigate([url]);
  }

  private initializeTableData(): Observable<PaginatedTableData> {
    // Combine all filter parameters with debounce for search
    const combinedParams$ = combineLatest([
      this.paginationParams$,
      this.searchQuery$.pipe(debounceTime(300), distinctUntilChanged()),
      this.advisorFilter$.pipe(distinctUntilChanged()),
      this.statusFilter$.pipe(distinctUntilChanged()),
    ]).pipe(
      map(([pagination, search, advisor, status]) => ({
        ...pagination,
        search: search || undefined,
        advisorFilter: advisor || undefined,
        statusFilter: status.length > 0 ? status : undefined,
      })),
    );

    return combineLatest([this.authService.currentAgentSessionOrAuthError, combinedParams$]).pipe(
      switchMap(([session, params]) => {
        this.agent = session.agent;

        const excludeSelfServe = true; // Always exclude self-serve like old table

        return this.loadPaginatedData(params, excludeSelfServe);
      }),
      shareReplay(1),
    );
  }

  private loadPaginatedData(
    params: PaginationParams,
    excludeSelfServe: boolean,
  ): Observable<PaginatedTableData> {
    return of({entries: [], totalRecords: 0, loading: true}).pipe(
      startWith({entries: [], totalRecords: 0, loading: true}),
      switchMap(() => {
        // Get total counts first to determine sequential loading strategy
        return forkJoin([
          this.getProposalCount(params, excludeSelfServe),
          this.getSaleReportCount(params, excludeSelfServe),
        ]).pipe(
          switchMap(([proposalCount, saleReportCount]) => {
            this.proposalCount = proposalCount;
            this.saleReportCount = saleReportCount;

            const totalRecords = proposalCount + saleReportCount;
            const proposalPages = Math.ceil(proposalCount / params.limit);

            // Sequential loading: proposals first, then sale reports
            if (params.page <= proposalPages) {
              // Load from proposals service
              return this.proposalService
                .getAllPaginated(params, excludeSelfServe)
                .pipe(switchMap((response) => this.processProposalData(response, totalRecords)));
            } else {
              // Load from sale reports service with adjusted page number
              const adjustedPage = params.page - proposalPages;
              const adjustedParams = {...params, page: adjustedPage};

              return this.saleReportService
                .getAllPaginated(adjustedParams, excludeSelfServe, true, true)
                .pipe(switchMap((response) => this.processSaleReportData(response, totalRecords)));
            }
          }),
        );
      }),
    );
  }

  private getProposalCount(params: PaginationParams, excludeSelfServe: boolean): Observable<number> {
    // Get first page to extract total count
    const countParams = {...params, page: 1, limit: 1};
    return this.proposalService
      .getAllPaginated(countParams, excludeSelfServe)
      .pipe(map((response) => response.total));
  }

  private getSaleReportCount(params: PaginationParams, excludeSelfServe: boolean): Observable<number> {
    // Get first page to extract total count
    const countParams = {...params, page: 1, limit: 1};
    return this.saleReportService
      .getAllPaginated(countParams, excludeSelfServe, true, true)
      .pipe(map((response) => response.total));
  }

  private processProposalData(
    response: PaginatedResponse<MinifiedProposal>,
    totalRecords: number,
  ): Observable<PaginatedTableData> {
    if (response.data.length === 0) {
      return of({entries: [], totalRecords, loading: false});
    }

    const agentIds = new Set<string>(
      response.data
        .flatMap((p) => p.agents!.filter((a) => !(a.agentId in InsuranceFirm)))
        .map((a) => a.agentId),
    );

    return this.agentService.getAgentsFromIds([...agentIds]).pipe(
      first(),
      map((agents) => {
        const agentNamesMap = new Map<string, string>();
        agents.forEach((a) => agentNamesMap.set(a.id, a.fullName));

        const entries = this.mapProposalsToTableEntries(response.data, agentNamesMap);
        return {entries, totalRecords, loading: false};
      }),
    );
  }

  private processSaleReportData(
    response: PaginatedResponse<MinifiedSaleReport>,
    totalRecords: number,
  ): Observable<PaginatedTableData> {
    const entries = this.mapSaleReportsToTableEntries(response.data);
    return of({entries, totalRecords, loading: false});
  }

  private mapProposalsToTableEntries(
    proposals: MinifiedProposal[],
    agentNamesMap: Map<string, string>,
  ): TableEntry[] {
    return proposals.map((proposal) => {
      const primaryAgent = proposal.agents?.find((a) => a.isPrimary);

      if (!primaryAgent) {
        throw new Error('A proposal should always have a primary agent.');
      }

      const status = this.getProposalStatusFromProposal(proposal);

      const proposalTableEntry: TableEntry = {
        agent: agentNamesMap.get(primaryAgent.agentId),
        proposalId: proposal.id,
        fullName: `${proposal.submitter?.firstName ?? ''} ${proposal.submitter?.lastName ?? ''}`,
        phone: proposal.submitter?.phone,
        status: {text: this.translate.getTranslation(status)!, value: status},
        clientId: proposal.userId,
        products: proposal.protection ? this.getTranslatedProducts(proposal.protection.personInsurances) : [],
        monthlyPrice: proposal.protection ? getMonthlyPrice(proposal.protection.personInsurances) : 0,
      };

      return proposalTableEntry;
    });
  }

  private mapSaleReportsToTableEntries(saleReports: MinifiedSaleReport[]): TableEntry[] {
    return saleReports.map((saleReport) => {
      const primaryAgent = saleReport.proposal.agents.find((agent) => agent.isPrimary);

      if (!primaryAgent) {
        throw new Error('No primary agent found for sale report');
      }

      const status = this.getProposalStatusFromSaleReport(saleReport);

      const saleReportTableEntry: TableEntry = {
        agent: `${primaryAgent.firstName} ${primaryAgent.lastName}`,
        policyNumber: saleReport.policyNumber,
        proposalId: saleReport.proposal.id,
        fullName: `${saleReport.proposal.submitter.firstName} ${saleReport.proposal.submitter.lastName}`,
        phone: saleReport.proposal.submitter.phone,
        status: {text: this.translate.getTranslation(status)!, value: status},
        clientId: saleReport.userId,
        products: this.getTranslatedProducts(saleReport.protection.personInsurances),
        monthlyPrice: getMonthlyPrice(saleReport.protection.personInsurances),
      };

      return saleReportTableEntry;
    });
  }

  private getProposalStatusFromProposal(proposal: MinifiedProposal): ProposalStatus {
    if (!proposal.extra?.sentAt) {
      return ProposalStatus.ONGOING;
    }

    if (!proposal.submittedAt) {
      return ProposalStatus.PENDING_CHECKOUT;
    }

    this.logger.warn('Proposal status not found', {proposalId: proposal.id});
    return ProposalStatus.ONGOING;
  }

  private getProposalStatusFromSaleReport(saleReport: MinifiedSaleReport): ProposalStatus {
    if (
      saleReport.status === SaleReportStatus.APPROVED_BY_INSURER &&
      saleReport.modificationNotice &&
      !saleReport.modificationDecisionMadeAt
    ) {
      return ProposalStatus.PENDING_MODIFICATION_DECISION;
    }

    if (
      saleReport.status === SaleReportStatus.APPROVED_BY_INSURER ||
      saleReport.status === SaleReportStatus.IN_FORCE
    ) {
      return ProposalStatus.APPROVED;
    }

    if (
      saleReport.status === SaleReportStatus.REFUSED_BY_INSURER ||
      saleReport.status === SaleReportStatus.REFUSED_BY_CLIENT
    ) {
      return ProposalStatus.REFUSED;
    }

    if (saleReport.status === SaleReportStatus.ABANDONED) {
      return ProposalStatus.ABANDONED;
    }

    const eqsCompleted = saleReport.proposal.healthAnswersRevisions?.every((har) => har.completedAt) ?? true;
    if (!eqsCompleted) {
      return ProposalStatus.PENDING_EXTRA_QUESTIONNAIRES;
    }

    if (saleReport.status === SaleReportStatus.UNDERWRITING) {
      if (saleReport.proposal.underwritingProfile?.extraRequirementsFile) {
        const {hasDocument, extraRequirementsInquiries} =
          saleReport.proposal.underwritingProfile.extraRequirementsFile;
        return extraRequirementsInquiries.length && !hasDocument
          ? ProposalStatus.PENDING_EXTERNAL_ACTION
          : ProposalStatus.PENDING_UNDERWRITING;
      }
      return ProposalStatus.PENDING_UNDERWRITING;
    }

    this.logger.warn('Proposal status not found', {
      saleReportId: saleReport.id,
      saleReportStatus: saleReport.status,
    });
    return ProposalStatus.ONGOING;
  }

  private getTranslatedProducts(
    personInsurances:
      | Pick<PersonInsurance, 'duration' | 'riders'>[]
      | Pick<ArchivedPersonInsurance, 'duration' | 'riders'>[],
  ): Product[] {
    return getProducts(personInsurances).map((product) => ({
      ...product,
      name:
        product.type !== ProductType.RIDER
          ? product.name
          : (this.translate.getTranslation(product.name) ?? ''),
    }));
  }

  private handlePhoneNumberCase(value: string): string {
    if (/(\+1|\(|\)|-)/.test(value)) {
      value = trimPhoneNumber(value);
    }
    return value;
  }

  // URL state management
  private initializeFromUrl(): void {
    const queryParams = this.route.snapshot.queryParams;

    // Initialize pagination parameters from URL
    const page = queryParams['page'] ? parseInt(queryParams['page'], 10) : 1;
    const limit = queryParams['limit'] ? parseInt(queryParams['limit'], 10) : this.defaultRowsPerPage;
    const search = queryParams['search'] || '';
    const advisor = queryParams['advisor'] || '';
    const statusStrings = queryParams['status'] ? queryParams['status'].split(',') : [];
    const statusEnums = statusStrings.filter((value): value is ProposalStatus =>
      Object.values(ProposalStatus).includes(value as ProposalStatus),
    );

    this.paginationParams$.next({page, limit});
    this.searchQuery$.next(search);
    this.advisorFilter$.next(advisor);
    this.statusFilter$.next(statusEnums);
  }

  private subscribeToStateChanges(): void {
    // Update URL when state changes
    combineLatest([
      this.paginationParams$,
      this.searchQuery$.pipe(debounceTime(300)),
      this.advisorFilter$,
      this.statusFilter$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([pagination, search, advisor, status]) => {
        const queryParams: any = {
          page: pagination.page,
          limit: pagination.limit,
        };

        if (search) {
          queryParams.search = search;
        }
        if (advisor) {
          queryParams.advisor = advisor;
        }
        if (status.length > 0) {
          queryParams.status = status.join(',');
        }

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams,
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });
  }
}
